import type { Promotion } from '@promotion/types'

export const useRewardChecks = () => {
    const rewardTypes = {
            freeSpins: 'FreeSpinsConfiguration',
            scratchCard: 'ScratchCardConfiguration',
            wheelOfFortune: 'WheelOfFortuneConfiguration',
            dropTheBall: 'DropTheBallConfiguration',
            bingoTicket: 'BingoFreeTicketConfiguration',
            signup: 'Bundle',
            bonus: 'Bonus',
            bonusPromotion: 'NewBonus',
        },
        triggerTypes = {
            signup: 5,
            login: 2,
            streak: 12,
        },
        REQUIREMENT_TYPE_PAYMENT = 'Payment',
        REQUIREMENT_TYPE_SPEND = 'Spend',
        STATUS_PENDING_REQUIREMENT = 'Pending requirement',
        BONUS_STATUS_PENDING_TERMS_ACCEPTED = 'PendingTermsAccepted',

        STATUS_ACTIVE = 'Active',
        STATUS_PENDING = 'Pending',
        STATUS_UNDECIDED = 'Undecided',

        isRewardType = (reward: Promotion, type: string) => {
            return reward.type === type || reward.foreign_model === type || type in reward
        },

        hasTurnOverMultiplier = (reward: Promotion) => Number(reward.turnover_multiplier) > 0,

        isScratchCards = (promotion: Promotion) => isRewardType(promotion, rewardTypes.scratchCard),

        isSignupBonusSpins = (promotion: Promotion) => isRewardType(promotion, rewardTypes.freeSpins)
            && Number(promotion.trigger_type) === triggerTypes.signup
            && promotion.requirement_type === REQUIREMENT_TYPE_PAYMENT,

        isBonusSpins = (promotion: Promotion) => isRewardType(promotion, rewardTypes.freeSpins),

        isSignupPromotion = (promotion: Promotion) => isRewardType(promotion, rewardTypes.signup),

        isBonus = (promotion: Promotion) => isRewardType(promotion, rewardTypes.bonus),

        isWheelOfFortuneSpins = (promotion: Promotion) => isRewardType(promotion, rewardTypes.wheelOfFortune),

        isDropTheBall = (promotion: Promotion) => isRewardType(promotion, rewardTypes.dropTheBall),

        isBingoTickets = (promotion: Promotion) => isRewardType(promotion, rewardTypes.bingoTicket),

        isBonusCash = (promotion: Promotion) => {
            if (isBonus(promotion) && Number(promotion.amount)) return true
            // until we restructure promotion-naming to not include 'NewBonus' - this is the way.
            if (isRewardType(promotion, rewardTypes.bonusPromotion) && Number(promotion.NewBonus?.amount)) return true

            return false
        },

        isValidForeignModel = (foreignModel: string) => {
            return foreignModel === rewardTypes.freeSpins || rewardTypes.scratchCard || rewardTypes.wheelOfFortune || rewardTypes.bingoTicket || rewardTypes.bonus || rewardTypes.bonusPromotion
        },

        isBonusPendingPaymentRequirement = (reward: Promotion) => reward.requirement_type === REQUIREMENT_TYPE_PAYMENT
            && reward.bonusOfferStatus === BONUS_STATUS_PENDING_TERMS_ACCEPTED,

        isPromotionPendingPaymentRequirement = (reward: Promotion) => reward.requirement_type === REQUIREMENT_TYPE_PAYMENT && reward.status === STATUS_PENDING_REQUIREMENT,

        isPromotionPendingSpendRequirement = (reward: Promotion) => reward.requirement_type === REQUIREMENT_TYPE_SPEND && reward.status === STATUS_PENDING_REQUIREMENT,

        isSignupPromotionPendingPayment = (reward: Promotion) => isSignupPromotion(reward) && reward.requirement_type === REQUIREMENT_TYPE_PAYMENT,

        isPaymentPendingRequirement = (reward: Promotion): boolean => {
            if (isSignupPromotion(reward)) {
                return isSignupPromotionPendingPayment(reward)
            }
            if (isBonus(reward)) {
                // Promotion-service cash offers carry no bonusOfferStatus; they pend on the promotion status.
                return isBonusPendingPaymentRequirement(reward) || isPromotionPendingPaymentRequirement(reward)
            }
            return isPromotionPendingPaymentRequirement(reward)
        },

        showTurnOverMultiplier = (reward: Promotion): boolean => {
            return Number(reward.turnover_multiplier) > 1
        }

    return {
        rewardTypes,
        triggerTypes,
        REQUIREMENT_TYPE_PAYMENT,
        REQUIREMENT_TYPE_SPEND,
        STATUS_PENDING_REQUIREMENT,
        BONUS_STATUS_PENDING_TERMS_ACCEPTED,
        STATUS_ACTIVE,
        STATUS_PENDING,
        STATUS_UNDECIDED,
        isSignupPromotion,
        isBonus,
        isBonusCash,
        isBonusSpins,
        isBingoTickets,
        isScratchCards,
        isSignupBonusSpins,
        isValidForeignModel,
        isWheelOfFortuneSpins,
        isDropTheBall,
        showTurnOverMultiplier,
        hasTurnOverMultiplier,
        isBonusPendingPaymentRequirement,
        isPromotionPendingPaymentRequirement,
        isPromotionPendingSpendRequirement,
        isSignupPromotionPendingPayment,
        isPaymentPendingRequirement
    }
}
